import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SUPPORTED_LANGS } from "@/i18n/config";

interface LangSwitcherProps {
  compact?: boolean;
}

export const LangSwitcher = forwardRef<HTMLDivElement, LangSwitcherProps>(
  ({ compact = false }, ref) => {
    const { i18n, t } = useTranslation("header");
    const current = i18n.language?.split("-")[0] || "fr";
    const next = current === "fr" ? "ar" : "fr";

    const switchTo = (lang: string) => {
      void i18n.changeLanguage(lang);
    };

    if (compact) {
      return (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => switchTo(next)}
          className="gap-2 text-foreground hover:bg-muted"
          aria-label={t("lang_label")}
        >
          <Languages className="size-4" />
          <span className="font-semibold uppercase">{next}</span>
        </Button>
      );
    }

    return (
      <div
        ref={ref}
        className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1"
        role="group"
        aria-label={t("lang_label")}
      >
        {SUPPORTED_LANGS.map((lang) => {
          const active = current === lang;
          return (
            <button
              key={lang}
              type="button"
              onClick={() => switchTo(lang)}
              className={`rounded-full px-3 py-1 text-xs font-semibold uppercase transition-colors ${
                active
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-pressed={active}
            >
              {lang}
            </button>
          );
        })}
      </div>
    );
  },
);
LangSwitcher.displayName = "LangSwitcher";
